import { buffItemPresentation } from '@/buffs/presentation'
import type { BuffSnapshot } from '@/buffs/types'
import type { StreamHudData } from './stream-hud'
import type { ExpRateChartSnapshot } from '@/domain/types'

/** Compare the actual icon presentation, including evidence expiry, rather than its clock. */
export function buffPresentationKey(snapshot: BuffSnapshot | null): string {
  return JSON.stringify(snapshot && [snapshot.locale, snapshot.state, snapshot.layout, snapshot.alerts,
    snapshot.items.map(item => [item.id, item.name, item.imageUrl, buffItemPresentation(item, snapshot)])])
}

function reuse<T>(previous: T, next: T): T {
  return JSON.stringify(previous) === JSON.stringify(next) ? previous : next
}

function chartPresentationKey(chart: ExpRateChartSnapshot | undefined): string | undefined {
  if (!chart) return undefined
  const recent = chart.recent
  // Evidence timestamps are consumed by the recognition pipeline, not the widget.
  // Axis/point times remain significant because they determine geometry and accessible labels.
  return JSON.stringify([chart.from, chart.to, chart.points, chart.gaps, chart.average,
    recent.value, recent.status, recent.windowMs,
    recent.continuousMs === undefined ? null : Math.floor(recent.continuousMs / 1000)])
}

/** Keep unchanged child props stable when another part of the widget updates. */
export function reuseWidgetData(previous: StreamHudData | undefined, next: StreamHudData): StreamHudData {
  if (!previous) return next
  const data = { ...next, expChart: chartPresentationKey(previous.expChart) === chartPresentationKey(next.expChart) ? previous.expChart : next.expChart,
    expRateTrend: reuse(previous.expRateTrend, next.expRateTrend),
    expReadout: reuse(previous.expReadout, next.expReadout), enabled: reuse(previous.enabled, next.enabled) }
  return reuse(previous, data)
}
